// Client/src/services/reinigungService.ts

import {
  IReinigungErledigungRequest,
  IReinigungErledigungResponse,
  IReinigungHistorie,
  IUnterschriftDetails,
  IReinigungsBeanstandung
} from '../types/ReinigungTypes';
import { AllRoutesTypes } from '../types/AllRoutesTypes';
import { isSameDay, differenceInCalendarDays } from 'date-fns';
import { httpClient } from '../core/SessionManager';

const API_BASE = '/api/reinigung';

const EMPTY_REINIGUNG_COUNTS = {
  pending: 0,
  pendingApproval: 0,
  dueSoon: 0,
  overdue: 0,
  pendingReview: 0,
  openBeanstandung: 0,
};

const getHttpStatus = (error: unknown): number | null => {
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    if (typeof record.status === 'number') return record.status;
    const response = record.response;
    if (response && typeof response === 'object') {
      const status = (response as Record<string, unknown>).status;
      if (typeof status === 'number') return status;
    }
  }
  return null;
};

interface ReinigungReviewRequest {
  bestanden: boolean;
  bemerkung?: string;
  attachments?: File[];
}

interface ReinigungBeanstandungRequest {
  bemerkung?: string;
  attachments?: File[];
}

interface ManualReinigungBeanstandungRequest {
  title: string;
  bemerkung?: string;
  attachments?: File[];
}

interface ReinigungReviewResponse {
  message: string;
  erledigung: AllRoutesTypes.IReinigungErledigung;
  beanstandung?: AllRoutesTypes.IReinigungsBeanstandung;
}

interface ReinigungBeanstandungResponse {
  message: string;
  erledigung: AllRoutesTypes.IReinigungErledigung;
  beanstandung: AllRoutesTypes.IReinigungsBeanstandung;
}

interface ManualReinigungBeanstandungResponse {
  message: string;
  reinigung: AllRoutesTypes.IReinigungsItem;
  beanstandung: AllRoutesTypes.IReinigungsBeanstandung;
}

interface ReinigungBeanstandungListResponse {
  beanstandungen: IReinigungsBeanstandung[];
}

interface ReinigungBeanstandungDetailResponse {
  beanstandung: IReinigungsBeanstandung;
}

interface ReinigungBeanstandungReportResponse {
  beanstandungen: IReinigungsBeanstandung[];
}

/**
 * Markiert eine Reinigungsaufgabe als erledigt mit Unterschrift
 */
export const markReinigungAsCompleted = async (
  reinigungId: string,
  data: IReinigungErledigungRequest
): Promise<IReinigungErledigungResponse> => {
  return httpClient.post<IReinigungErledigungResponse, IReinigungErledigungRequest>(
    `${API_BASE}/${reinigungId}/erledigung`,
    data
  );
};

/**
 * Holt die Historie aller Erledigungen einer Reinigungsaufgabe
 */
export const getReinigungHistorie = async (reinigungId: string): Promise<IReinigungHistorie> => {
  return httpClient.get<IReinigungHistorie>(`${API_BASE}/${reinigungId}/erledigungen`);
};

/**
 * Holt eine spezifische Unterschrift mit allen Details
 */
export const getUnterschriftDetails = async (erledigungId: string): Promise<IUnterschriftDetails> => {
  return httpClient.get<IUnterschriftDetails>(`${API_BASE}/erledigung/${erledigungId}/unterschrift`);
};

/**
 * Holt die zweite Unterschrift (für separate Abrechnung) mit allen Details
 */
export const getZweiteUnterschriftDetails = async (erledigungId: string): Promise<IUnterschriftDetails> => {
  return httpClient.get<IUnterschriftDetails>(`${API_BASE}/erledigung/${erledigungId}/zweite-unterschrift`);
};

/**
 * Holt alle Reinigungsaufgaben für einen Store
 */
export const getReinigungsaufgaben = async (storeId: string): Promise<AllRoutesTypes.IReinigungsItem[]> => {
  return httpClient.get<AllRoutesTypes.IReinigungsItem[]>(`${API_BASE}/store/${storeId}`);
};

/**
 * Prüft eine erledigte Reinigung (Bestanden oder Beanstandung)
 */
export const reviewReinigungErledigung = async (
  erledigungId: string,
  payload: ReinigungReviewRequest
): Promise<ReinigungReviewResponse> => {
  const formData = new FormData();
  formData.append('bestanden', payload.bestanden ? 'true' : 'false');
  if (payload.bemerkung) {
    formData.append('bemerkung', payload.bemerkung);
  }
  payload.attachments?.forEach((file) => formData.append('attachments', file));

  return httpClient.post<ReinigungReviewResponse>(
    `${API_BASE}/erledigung/${erledigungId}/review`,
    formData,
    { headers: {} }
  );
};

/**
 * Legt eine Beanstandung zu einer Erledigung an
 */
export const createReinigungBeanstandung = async (
  erledigungId: string,
  payload: ReinigungBeanstandungRequest
): Promise<ReinigungBeanstandungResponse> => {
  const formData = new FormData();
  if (payload.bemerkung) {
    formData.append('bemerkung', payload.bemerkung);
  }
  payload.attachments?.forEach((file) => formData.append('attachments', file));

  return httpClient.post<ReinigungBeanstandungResponse>(
    `${API_BASE}/erledigung/${erledigungId}/beanstandung`,
    formData,
    { headers: {} }
  );
};

/**
 * Legt eine Beanstandung ohne bestehende Aufgabe an (Ad-hoc)
 */
export const createManualReinigungsBeanstandung = async (
  storeId: string,
  payload: ManualReinigungBeanstandungRequest
): Promise<ManualReinigungBeanstandungResponse> => {
  const formData = new FormData();
  formData.append('title', payload.title);
  if (payload.bemerkung) {
    formData.append('bemerkung', payload.bemerkung);
  }
  payload.attachments?.forEach((file) => formData.append('attachments', file));

  return httpClient.post<ManualReinigungBeanstandungResponse>(
    `${API_BASE}/store/${storeId}/beanstandung/manual`,
    formData,
    { headers: {} }
  );
};

/**
 * Holt alle Beanstandungen für einen Store (optional nach Status gefiltert)
 */
export const listReinigungsBeanstandungen = async (
  storeId: string,
  status?: 'open' | 'acknowledged' | 'closed'
): Promise<ReinigungBeanstandungListResponse> => {
  const query = new URLSearchParams({ storeId });
  if (status) {
    query.set('status', status);
  }
  return httpClient.get<ReinigungBeanstandungListResponse>(`${API_BASE}/beanstandungen?${query.toString()}`);
};

/**
 * Holt Detailinformationen zu einer Beanstandung
 */
export const getReinigungsBeanstandung = async (
  beanstandungId: string
): Promise<ReinigungBeanstandungDetailResponse> => {
  return httpClient.get<ReinigungBeanstandungDetailResponse>(`${API_BASE}/beanstandung/${beanstandungId}`);
};

/**
 * Markiert eine Beanstandung als zur Kenntnis genommen
 */
export const acknowledgeReinigungsBeanstandung = async (
  beanstandungId: string
): Promise<ReinigungBeanstandungDetailResponse> => {
  return httpClient.post<ReinigungBeanstandungDetailResponse>(
    `${API_BASE}/beanstandung/${beanstandungId}/acknowledge`
  );
};

/**
 * Holt Beanstandungen eines Stores für einen Zeitraum (inkl. Bilder & Signaturen)
 */
export const getReinigungBeanstandungReport = async (
  storeId: string,
  options?: { from?: string; to?: string; status?: 'open' | 'acknowledged' | 'closed' }
): Promise<ReinigungBeanstandungReportResponse> => {
  const query = new URLSearchParams({ storeId });
  if (options?.from) {
    query.set('from', options.from);
  }
  if (options?.to) {
    query.set('to', options.to);
  }
  if (options?.status) {
    query.set('status', options.status);
  }
  return httpClient.get<ReinigungBeanstandungReportResponse>(`${API_BASE}/beanstandungen/report?${query.toString()}`);
};

/**
 * Zählt fällige und offene Reinigungsaufgaben für Navbar-Badge
 */
export const countReinigungsaufgaben = async (storeId: string): Promise<{
  pending: number;
  pendingApproval: number;
  dueSoon: number;
  overdue: number;
  pendingReview: number;
  openBeanstandung: number;
}> => {
  try {
    const tasks = await getReinigungsaufgaben(storeId);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

  let pending = 0;
  let pendingApproval = 0;
  let pendingReview = 0;
  let openBeanstandung = 0;
  let dueSoon = 0;
  let overdue = 0;

    const addDueSoon = () => {
      dueSoon++;
      pending++;
    };

    const addOverdue = () => {
      overdue++;
      pending++;
    };

    tasks.forEach(task => {
      if (!task.is_active) return;

      // Aufgaben mit offener Beanstandung priorisieren
      if (task.has_open_beanstandung) {
        openBeanstandung++;
        
        // Zähle Beanstandungen, die noch nicht nachbearbeitet wurden, als pending
        // open_beanstandung_meta?.reworked_at ist null/undefined wenn noch nicht nachbearbeitet
        if (!task.open_beanstandung_meta?.reworked_at) {
          pending++;
        }
      }

      // Aufgaben mit offener Prüfung
      if (task.has_pending_review) {
        pendingReview++;
      }

      // Zähle Aufgaben, die auf Freigabe warten (nur für Dashboard-Banner)
      if (task.has_pending_approval) {
        pendingApproval++;
        return; // Diese Aufgaben nicht zu pending hinzufügen
      }

      // Beanstandungen ohne Nacharbeit wurden bereits gezählt
      if (task.has_open_beanstandung && !task.open_beanstandung_meta?.reworked_at) {
        return; // Nicht doppelt zählen
      }

      // Zähle nur fällige oder zu erledigende Aufgaben (für Navbar-Badge)
      if (task.interval_type === 'none') {
        // Einmalige Aufgabe - nur zählen wenn noch nie erledigt
        if (task.last_executed_at === null) {
          const createdDate = new Date(task.createdAt || Date.now());
          createdDate.setHours(0, 0, 0, 0);
          const daysSinceCreation = differenceInCalendarDays(today, createdDate);
          
          // Erst nach 2 Tagen als überfällig markieren
          if (daysSinceCreation <= 2) {
            addDueSoon();
          } else {
            addOverdue();
          }
        }
        // Wenn last_executed_at gesetzt ist, ist die Aufgabe abgeschlossen
      } else if (task.interval_type === 'on_demand') {
        // Nach Bedarf Aufgaben - nur zählen wenn heute noch nicht erledigt
        const lastExecuted = task.last_executed_at ? new Date(task.last_executed_at) : null;
        if (!lastExecuted || !isSameDay(lastExecuted, today)) {
          addDueSoon();
        }
      } else if (task.next_due_date) {
        // Wiederkehrende Aufgabe mit Fälligkeitsdatum
        const dueDate = new Date(task.next_due_date);
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate <= today) {
          const daysOverdue = differenceInCalendarDays(today, dueDate);
          if (daysOverdue <= 2) {
            addDueSoon();
          } else {
            addOverdue();
          }
        }
      } else if (['daily', 'weekly', 'monthly', 'yearly'].includes(task.interval_type) && task.last_executed_at === null) {
        // Wiederkehrende Aufgabe, die noch nie erledigt wurde
        // Prüfe, ob die Aufgabe vor mehr als 2 Tagen erstellt wurde
        const createdDate = new Date(task.createdAt || Date.now());
        createdDate.setHours(0, 0, 0, 0);
        const daysSinceCreation = differenceInCalendarDays(today, createdDate);
        
        if (daysSinceCreation <= 2) {
          addDueSoon();
        } else {
          addOverdue();
        }
      }
    });

    return { pending, pendingApproval, dueSoon, overdue, pendingReview, openBeanstandung };
  } catch (error) {
    if (getHttpStatus(error) === 404) {
      console.warn(`Keine Reinigungsaufgaben fuer Store ${storeId} gefunden; Dashboard-Zaehler wird als leer behandelt.`);
      return { ...EMPTY_REINIGUNG_COUNTS };
    }

    console.error('Fehler beim Zählen der Reinigungsaufgaben:', error);
    return { ...EMPTY_REINIGUNG_COUNTS };
  }
};
